import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Box, IconButton, Tooltip, Popover, TextField, InputAdornment, List, ListItemButton,
  Typography, Chip, alpha,
} from '@mui/material';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Icons from './Icons';
import { khopNode } from './nodeSearch';

/**
 * CanvasNodeSearch — tìm node ĐANG CÓ TRÊN SƠ ĐỒ theo tên (tên đặt cho node, tên loại node)
 * hoặc theo công dụng (mô tả của loại node). Chọn một kết quả -> sơ đồ cuộn/zoom tới node đó.
 * Dùng được cả ở chế độ chỉ xem (chỉ di chuyển khung nhìn, không mở khung thuộc tính).
 *
 * props:
 *   nodes        — node trên sơ đồ (ReactFlow)
 *   definitions  — map type -> định nghĩa node (label, description...)
 *   onPick(node) — gọi khi chọn một node
 */
const CanvasNodeSearch = ({ nodes, definitions, onPick }) => {
  const nutRef = useRef(null);
  const [mo, setMo] = useState(false);
  const [q, setQ] = useState('');
  const [chon, setChon] = useState(0);

  const ketQua = useMemo(() => (nodes || [])
    .map((n) => {
      const def = (definitions && definitions[n.type]) || { type: n.type, label: n.type };
      const m = khopNode(def, q, 'all', n.data && n.data.name);
      return m ? { node: n, def, ...m } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 50), [nodes, definitions, q]);

  useEffect(() => { setChon(0); }, [q, mo]);

  const pick = (item) => {
    if (!item) return;
    setMo(false);
    onPick && onPick(item.node);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setChon((i) => Math.min(i + 1, ketQua.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setChon((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(ketQua[chon]); }
  };

  return (
    <>
      <Tooltip title="Tìm node trên sơ đồ (theo tên hoặc công dụng)">
        <IconButton ref={nutRef} size="small" onClick={() => setMo(true)}
          sx={{ p: '5px', border: '1px solid', borderColor: (t) => alpha(t.palette.divider, 0.6) }}>
          <ManageSearchRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={mo}
        anchorEl={nutRef.current}
        onClose={() => setMo(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 24px)', mt: 0.5 } } }}
      >
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            autoFocus fullWidth size="small" value={q} placeholder="Tên node hoặc công dụng…"
            onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
            {`${ketQua.length} / ${(nodes || []).length} node — ↑↓ để chọn, Enter để tới node`}
          </Typography>
        </Box>
        <List dense disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {ketQua.map((item, i) => (
            <ListItemButton key={item.node.id} selected={i === chon} onMouseEnter={() => setChon(i)} onClick={() => pick(item)}
              sx={{ alignItems: 'flex-start', gap: 1, py: 0.75 }}>
              <Box sx={{ width: 18, pt: '2px', display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
                <FontAwesomeIcon icon={Icons[item.node.type] || Icons.default} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {(item.node.data && item.node.data.name) || item.def.label || item.node.type}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {item.def.label || item.node.type}{item.node.data && item.node.data.disabled ? ' · đã vô hiệu' : ''}
                </Typography>
              </Box>
              {q && item.by === 'usage' && (
                <Chip label="công dụng" size="small" variant="outlined"
                  sx={{ height: 18, fontSize: 10, mt: '2px', '& .MuiChip-label': { px: 0.75 } }} />
              )}
            </ListItemButton>
          ))}
          {!ketQua.length && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              Không có node nào khớp.
            </Typography>
          )}
        </List>
      </Popover>
    </>
  );
};

export default CanvasNodeSearch;
